import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import 'bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { MeteogramComponent } from '../meteogram/meteogram.component';
import { Location } from '../shared/models/location.model';
import { MeteogramData } from '../shared/models/meteogram-data.model';
import { WeatherDataItem } from '../shared/models/weather-data-item.model';
import { LocationService } from '../shared/services/location.service';

@Component({
  selector: 'app-weather-data',
  templateUrl: './weather-data.component.html',
  styleUrls: ['./weather-data.component.css']
})

export class WeatherDataComponent implements OnInit, OnDestroy {
  windDirections: string[] = ['North', 'North-Northeast', 'Northeast', 'East-Northeast',
    'East', 'East-Southeast', 'Southeast', 'South-Southeast', 'South', 'South-Southwest',
    'Southwest', 'West-Southwest', 'West', 'West-Northwest', 'Northwest', 'North-Northwest'];
  locationSelModel: Location = {} as Location;
  locations: Location[];
  tableWeatherDataItems: {};
  noLocations = true;
  action: string;
  delDate: string;
  startDateModel: string;
  endDateModel: string;
  locationTxtModel: string;
  tempName: string;
  weatherDataForm: FormGroup;
  private locationsSub: Subscription;
  private allWeatherDataItems: {};
  private locationIndex: number;
  private prevName = '';
  private weatherDataDate: string;
  private lastUpdated: string;
  private submitted = false;
  @ViewChild(MeteogramComponent) meteogram: MeteogramComponent;
  @ViewChild('closeAddLocationBtn') closeAddLocationBtn;
  @ViewChild('closeDelLocationBtn') closeDelLocationBtn;
  @ViewChild('closeAddEditWeatherDataBtn') closeAddEditWeatherDataBtn;
  @ViewChild('closeDelWeatherDataBtn') closeDelWeatherDataBtn;

  constructor(
    private datePipe: DatePipe,
    private formBuilder: FormBuilder,
    private locationService: LocationService,
    private toastr: ToastrService
  ) {
    this.createWeatherDataForm();
  }

  ngOnInit(): void {
    this.locationsSub = this.locationService.getAll().subscribe((locations: Location[]) => {
      this.handleLocations(locations);
    });
  }

  ngOnDestroy(): void {
    if (!this.locationsSub.closed) {
      this.locationsSub.unsubscribe();
    }
  }

  onLocationChange(location: Location): void {
    this.locationIndex = this.getLocationIndex(this.locationSelModel.name);
    this.allWeatherDataItems = this.locationSelModel.weatherDataItems;
    this.handleFilterDates(location.name);
  }

  onSubmitLocation(form: NgForm): void {
    if (this.submitted) {
      return;
    }
    this.submitted = true;
    const name = this.locationTxtModel.trim();
    if (this.isDuplicateLocation(name)) {
      this.submitted = false;
      return;
    }
    if (!this.locationsSub.closed) {
      this.locationsSub.unsubscribe();
    }
    this.locationService.add(name)
    .then(() => {
      if (this.locations.length > 0) {
        this.prevName = name;
      }
      this.locationsSub = this.locationService.getAll().subscribe((locations: Location[]) => {
        this.handleLocations(locations);
      });
      this.submitted = false;
      this.closeAddLocationBtn.nativeElement.click();
      form.reset();
      console.log('Created a new location successfully.');
      this.toastr.success('New location added', 'Success');
    })
    .catch(() => {
      this.submitted = false;
      console.error('Failed to create a new location.');
      this.toastr.error('Could not add new location', 'Failure');
    });
  }

  delLocation(): void {
    this.tempName = this.locationSelModel.name;
  }

  onDeleteLocation(): void {
    if (this.submitted) {
      return;
    }
    this.submitted = true;
    if (!this.isLocation()) {
      return;
    }
    if (!this.locationsSub.closed) {
      this.locationsSub.unsubscribe();
    }
    this.locationService.delete(this.locationSelModel.name)
    .then(() => {
      this.locationsSub = this.locationService.getAll().subscribe((locations: Location[]) => {
        this.handleLocations(locations);
      });
      this.submitted = false;
      this.closeDelLocationBtn.nativeElement.click();
      console.log('Deleted the location successfully.');
      this.toastr.success('Location deleted', 'Success');
    })
    .catch(() => {
      this.submitted = false;
      console.error('Failed to delete the location.');
      this.toastr.error('Could not delete location', 'Failure');
    });
  }

  addWeatherData(): void {
    this.action = 'Add';
    this.tempName = this.locationSelModel.name;
    if (this.weatherDataDate) {
      this.weatherDataDate = undefined;
      this.weatherDataForm.reset();
      this.weatherDataForm.patchValue({
        windDirectionSel: 'North'
      });
    }
  }

  onSubmitWeatherData(): void {
    if (this.submitted) {
      return;
    }
    this.submitted = true;
    if (!this.isLocation()) {
      return;
    }
    if (this.action === 'Add' && this.isDuplicateOnAddWeatherData()) {
      this.submitted = false;
      return;
    } else if (this.action === 'Edit' && !this.isValidData()) {
      this.submitted = false;
      return;
    }
    if (!this.locationsSub.closed) {
      this.locationsSub.unsubscribe();
    }
    this.locationService.update(this.locationSelModel.name, this.getAddedWeatherData())
    .then(() => {
      if (this.locations.length > 1) {
        this.prevName = this.locationSelModel.name;
      }
      this.locationsSub = this.locationService.getAll().subscribe((locations: Location[]) => {
        this.handleLocations(locations);
      });
      this.submitted = false;
      this.closeAddEditWeatherDataBtn.nativeElement.click();
      this.weatherDataForm.reset();
      this.weatherDataForm.patchValue({
        windDirectionSel: 'North'
      });
      if (this.action === 'Add') {
        console.log('Created new weather data successfully.');
        this.toastr.success('New weather data added', 'Success');
      } else if (this.action === 'Edit') {
        console.log('Updated the weather data successfully.');
        this.toastr.success('Weather data updated', 'Success');
      }
    })
    .catch(() => {
      this.submitted = false;
      if (this.action === 'Add') {
        console.error('Failed to create new weather data.');
        this.toastr.error('Could not add new weather data', 'Failure');
      } else if (this.action === 'Edit') {
        console.error('Failed to update the weather data.');
        this.toastr.error('Could not update weather data', 'Failure');
      }
    });
  }

  onDeleteWeatherData(): void {
    if (this.submitted) {
      return;
    }
    this.submitted = true;
    if (!this.isLocation()) {
      return;
    }
    if (!this.isWeatherData()) {
      this.submitted = false;
      return;
    }
    const items = this.locations[this.locationIndex].weatherDataItems;
    delete items[this.weatherDataDate];
    let data: any;
    if (Object.keys(items).length > 0) {
      data = items;
    } else {
      data = '';
    }
    if (!this.locationsSub.closed) {
      this.locationsSub.unsubscribe();
    }
    this.locationService.update(this.locationSelModel.name, { weatherDataItems: data })
    .then(() => {
      if (this.locations.length > 1) {
        this.prevName = this.locationSelModel.name;
      }
      this.locationsSub = this.locationService.getAll().subscribe((locations: Location[]) => {
        this.handleLocations(locations);
      });
      this.submitted = false;
      this.closeDelWeatherDataBtn.nativeElement.click();
      console.log('Deleted the weather data successfully.');
      this.toastr.success('Weather data deleted', 'Success');
    })
    .catch(() => {
      this.submitted = false;
      console.error('Failed to delete the weather data.');
      this.toastr.error('Could not delete weather data', 'Failure');
    });
  }

  onStartDateChange(date: string): void {
    let meteogramData = this.getInitedMeteogramData(this.locationSelModel.name);
    this.tableWeatherDataItems = {};
    const end = new Date(this.endDateModel);
    if (date) {
      const start = new Date(date);
      for (const key of Object.keys(this.allWeatherDataItems)) {
        const itemDate = new Date(key);
        if (!this.endDateModel) {
          if (itemDate.getTime() >= start.getTime()) {
            meteogramData = this.getAddedMeteogramData(meteogramData, key);
            const value = this.allWeatherDataItems[key];
            this.tableWeatherDataItems[key] = this.getTableItemValues(value);
          }
        } else {
          if (itemDate.getTime() >= start.getTime() && itemDate.getTime() <= end.getTime()) {
            meteogramData = this.getAddedMeteogramData(meteogramData, key);
            const value = this.allWeatherDataItems[key];
            this.tableWeatherDataItems[key] = this.getTableItemValues(value);
          }
        }
      }
      this.tableWeatherDataItems = this.getSortedWeatherData(this.tableWeatherDataItems);
    } else if (!date && this.endDateModel) {
      for (const key of Object.keys(this.allWeatherDataItems)) {
        const itemDate = new Date(key);
        if (itemDate.getTime() <= end.getTime()) {
          meteogramData = this.getAddedMeteogramData(meteogramData, key);
          const value = this.allWeatherDataItems[key];
          this.tableWeatherDataItems[key] = this.getTableItemValues(value);
        }
      }
      this.tableWeatherDataItems = this.getSortedWeatherData(this.tableWeatherDataItems);
    } else {
      for (const key of Object.keys(this.allWeatherDataItems)) {
        meteogramData = this.getAddedMeteogramData(meteogramData, key);
      }
      this.tableWeatherDataItems = this.getSortedWeatherData({...this.allWeatherDataItems});
    }
    this.meteogram.draw(meteogramData);
  }

  onEndDateChange(date: string): void {
    let meteogramData = this.getInitedMeteogramData(this.locationSelModel.name);
    this.tableWeatherDataItems = {};
    const start = new Date(this.startDateModel);
    if (date) {
      const end = new Date(date);
      for (const key of Object.keys(this.allWeatherDataItems)) {
        const itemDate = new Date(key);
        if (!this.startDateModel) {
          if (itemDate.getTime() <= end.getTime()) {
            meteogramData = this.getAddedMeteogramData(meteogramData, key);
            const value = this.allWeatherDataItems[key];
            this.tableWeatherDataItems[key] = this.getTableItemValues(value);
          }
        } else {
          if (itemDate.getTime() >= start.getTime() && itemDate.getTime() <= end.getTime()) {
            meteogramData = this.getAddedMeteogramData(meteogramData, key);
            const value = this.allWeatherDataItems[key];
            this.tableWeatherDataItems[key] = this.getTableItemValues(value);
          }
        }
      }
      this.tableWeatherDataItems = this.getSortedWeatherData(this.tableWeatherDataItems);
    } else if (this.startDateModel && !date) {
      for (const key of Object.keys(this.allWeatherDataItems)) {
        const itemDate = new Date(key);
        if (itemDate.getTime() >= start.getTime()) {
          meteogramData = this.getAddedMeteogramData(meteogramData, key);
          const value = this.allWeatherDataItems[key];
          this.tableWeatherDataItems[key] = this.getTableItemValues(value);
        }
      }
      this.tableWeatherDataItems = this.getSortedWeatherData(this.tableWeatherDataItems);
    } else {
      for (const key of Object.keys(this.allWeatherDataItems)) {
        meteogramData = this.getAddedMeteogramData(meteogramData, key);
      }
      this.tableWeatherDataItems = this.getSortedWeatherData({...this.allWeatherDataItems});
    }
    this.meteogram.draw(meteogramData);
  }

  returnZero(): number {
    return 0;
  }

  private getSortedWeatherData(items: {}): {} {
    const sorted = {};
    Object.keys(items).sort((a, b) => {
      return b.localeCompare(a);
    }).forEach(key => {
      sorted[key] = items[key];
    });
    return sorted;
  }

  private initValues(): void {
    this.locationSelModel = {} as Location;
    this.locations = [];
    this.tableWeatherDataItems = {};
    this.meteogram.draw(this.getInitedMeteogramData(''));
  }

  private handleLocations(locations: Location[]): void {
    if (locations.length === 0) {
      this.noLocations = true;
      this.initValues();
      console.log('No locations.');
    } else {
      this.locations = locations.sort((a, b) => a.name.localeCompare(b.name));
      this.noLocations = false;
      this.setLocations();
      console.log('Locations loaded successfully.');
    }
  }

  private getAddedWeatherData(): {} {
    const date = this.weatherDataForm.controls.dateDate.value;
    const item = this.getWeatherDataItemValues();
    let items = this.locations[this.locationIndex].weatherDataItems;
    if (items === '') {
      items = {
        [date]: item
      };
    } else {
      items[date] = item;
      if (this.action === 'Edit') {
        if (this.weatherDataDate !== date) {
          delete items[this.weatherDataDate];
        }
      }
    }
    return { weatherDataItems: items };
  }

  private isValidData(): boolean {
    const date = this.weatherDataForm.controls.dateDate.value;
    const items = this.locations[this.locationIndex].weatherDataItems;
    if (this.weatherDataDate !== date && !items[date]) {
      return true;
    }
    if (this.weatherDataDate === date && !items[date]) {
      console.error('Weather data does not exist anymore.');
      this.toastr.error('Weather data does not exist anymore', 'Failure');
      return false;
    }
    const updated = items[date].lastUpdated;
    if (this.weatherDataDate === date && this.lastUpdated === updated) {
      return true;
    }
    if (this.weatherDataDate !== date && items[date]) {
      console.error('Trying to create duplicate weather data.');
      this.toastr.error('Weather data already exists', 'Failure');
      return false;
    }
    if (this.weatherDataDate === date && this.lastUpdated !== updated) {
      console.error('Weather data has been updated by someone else.');
      this.toastr.error('Weather data has been updated by someone else', 'Failure');
      return false;
    }
  }

  private createWeatherDataForm(): void {
    this.weatherDataForm = this.formBuilder.group({
      dateDate: ['', Validators.required],
      temperatureNo: ['', [Validators.required, Validators.min(-150), Validators.max(150)]],
      rainfallNo: ['', [Validators.required, Validators.min(0), Validators.max(2500)]],
      windNo: ['', [Validators.required, Validators.min(0), Validators.max(450000)]],
      windDirectionSel: ['North']
    });
  }

  private setLocations(): void {
    if (this.prevName !== '') {
      this.locationIndex = this.getLocationIndex(this.prevName);
      this.setData(this.locations[this.locationIndex]);
      this.prevName = '';
    } else {
      this.locationIndex = 0;
      this.setData(this.locations[0]);
    }
  }

  private getAddedMeteogramData(data: MeteogramData, key: string): MeteogramData {
    const value = this.allWeatherDataItems[key];
    data.categories.push(this.getFormattedDate(key));
    data.temperatures.push(value.temperature);
    data.rainfalls.push(value.rainfall);
    data.winds.push(this.getWindData(this.allWeatherDataItems[key]));
    return data;
  }

  private getLocationIndex(name: string): number {
    for (let i = 0; i < this.locations.length; ++i) {
      if (this.locations[i].name === name) {
        return i;
      }
    }
  }

  private getWindData(item: WeatherDataItem): number[] {
    return [item.wind, this.getDirectionDegrees(item.windDirection)];
  }

  private getFormattedDate(date: string): string {
    return this.datePipe.transform(date, 'd.M.yyyy');
  }

  private getTableItemValues(value: any): WeatherDataItem {
    const item: WeatherDataItem = {} as WeatherDataItem;
    item.temperature = value.temperature;
    item.rainfall = value.rainfall;
    item.wind = value.wind;
    item.windDirection = value.windDirection;
    return item;
  }

  private handleFilterDates(name: string): void {
    let meteogramData = this.getInitedMeteogramData(name);
    this.tableWeatherDataItems = {};
    if (this.startDateModel || this.endDateModel) {
      const start = new Date(this.startDateModel);
      const end = new Date(this.endDateModel);
      if (this.startDateModel && this.endDateModel) {
        for (const key of Object.keys(this.allWeatherDataItems)) {
          const date = new Date(key);
          if (date.getTime() >= start.getTime() && date.getTime() <= end.getTime()) {
            meteogramData = this.getAddedMeteogramData(meteogramData, key);
            const value = this.allWeatherDataItems[key];
            this.tableWeatherDataItems[key] = this.getTableItemValues(value);
          }
        }
      } else if (this.startDateModel) {
        for (const key of Object.keys(this.allWeatherDataItems)) {
          const date = new Date(key);
          if (date.getTime() >= start.getTime()) {
            meteogramData = this.getAddedMeteogramData(meteogramData, key);
            const value = this.allWeatherDataItems[key];
            this.tableWeatherDataItems[key] = this.getTableItemValues(value);
          }
        }
      } else if (this.endDateModel) {
        for (const key of Object.keys(this.allWeatherDataItems)) {
          const date = new Date(key);
          if (date.getTime() <= end.getTime()) {
            meteogramData = this.getAddedMeteogramData(meteogramData, key);
            const value = this.allWeatherDataItems[key];
            this.tableWeatherDataItems[key] = this.getTableItemValues(value);
          }
        }
      }
      this.tableWeatherDataItems = this.getSortedWeatherData(this.tableWeatherDataItems);
    } else {
      for (const key of Object.keys(this.allWeatherDataItems)) {
        meteogramData = this.getAddedMeteogramData(meteogramData, key);
      }
      this.tableWeatherDataItems = this.getSortedWeatherData({...this.allWeatherDataItems});
    }
    this.meteogram.draw(meteogramData);
  }

  private setData(location: Location): void {
    this.locationSelModel = location;
    this.allWeatherDataItems = location.weatherDataItems;
    if (Object.keys(location.weatherDataItems).length > 0) {
      this.handleFilterDates(location.name);
    } else {
      this.tableWeatherDataItems = {};
      this.meteogram.draw(this.getInitedMeteogramData(location.name));
    }
  }

  private getInitedMeteogramData(name: string): MeteogramData {
    const data: MeteogramData = {} as MeteogramData;
    data.location = name;
    data.categories = [];
    data.temperatures = [];
    data.rainfalls = [];
    data.winds = [];
    return data;
  }

  private getDirectionDegrees(direction: string): number {
    if (direction === this.windDirections[0]) {
      return 0;
    } else if (direction === this.windDirections[1]) {
      return 22.5;
    } else if (direction === this.windDirections[2]) {
      return 45;
    } else if (direction === this.windDirections[3]) {
      return 67.5;
    } else if (direction === this.windDirections[4]) {
      return 90;
    } else if (direction === this.windDirections[5]) {
      return 112.5;
    } else if (direction === this.windDirections[6]) {
      return 135;
    } else if (direction === this.windDirections[7]) {
      return 157.5;
    } else if (direction === this.windDirections[8]) {
      return 180;
    } else if (direction === this.windDirections[9]) {
      return 202.5;
    } else if (direction === this.windDirections[10]) {
      return 225;
    } else if (direction === this.windDirections[11]) {
      return 247.5;
    } else if (direction === this.windDirections[12]) {
      return 270;
    } else if (direction === this.windDirections[13]) {
      return 292.5;
    } else if (direction === this.windDirections[14]) {
      return 315;
    } else if (direction === this.windDirections[15]) {
      return 337.5;
    }
  }

  private isDuplicateLocation(name: string): boolean {
    for (const location of this.locations) {
      if (location.name.toLowerCase() === name.toLowerCase()) {
        console.error('Trying to create a duplicate location.');
        this.toastr.error('Location already exists', 'Failure');
        return true;
      }
    }
    return false;
  }

  private isLocation(): boolean {
    for (const location of this.locations) {
      if (location.name.toLowerCase() === this.tempName.toLowerCase()) {
        return true;
      }
    }
    this.submitted = false;
    console.error('Location does not exist anymore.');
    this.toastr.error('Location does not exist anymore', 'Failure');
    return false;
  }

  private editWeatherData(item: any): void {
    this.action = 'Edit';
    this.tempName = this.locationSelModel.name;
    this.weatherDataDate = item.key;
    this.weatherDataForm.setValue({
      dateDate: item.key,
      temperatureNo: item.value.temperature,
      rainfallNo: item.value.rainfall,
      windNo: item.value.wind,
      windDirectionSel: item.value.windDirection
    });
    this.lastUpdated = item.value.lastUpdated;
  }

  private delWeatherData(item: any): void {
    this.delDate = this.getFormattedDate(item.key);
    this.tempName = this.locationSelModel.name;
    this.weatherDataDate = item.key;
  }

  private getWeatherDataItemValues(): WeatherDataItem {
    const item: WeatherDataItem = {} as WeatherDataItem;
    item.temperature = this.weatherDataForm.controls.temperatureNo.value;
    item.rainfall = this.weatherDataForm.controls.rainfallNo.value;
    item.wind = this.weatherDataForm.controls.windNo.value;
    item.windDirection = this.weatherDataForm.controls.windDirectionSel.value;
    item.lastUpdated = new Date().toISOString();
    return item;
  }

  private isDuplicateOnAddWeatherData(): boolean {
    const date = this.weatherDataForm.controls.dateDate.value;
    if (this.locations[this.locationIndex].weatherDataItems[date]) {
      console.error('Trying to create duplicate weather data.');
      this.toastr.error('Weather data already exists', 'Failure');
      return true;
    }
    return false;
  }

  private isWeatherData(): boolean {
    if (!this.locations[this.locationIndex].weatherDataItems[this.weatherDataDate]) {
      console.error('Weather data does not exist anymore.');
      this.toastr.error('Weather data does not exist anymore', 'Failure');
      return false;
    }
    return true;
  }
}
